import { DownOutlined, EyeOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Form, Input, Row, Select, Space, Table } from 'antd';
import withAuthorization from '../components/hocs/withAuthorization';
import PageWithFilters from '../components/page/page-with-filters';
import { PageWithFiltersContentContainer } from '../components/page/page.styles';
import { CardHoldersActionsContainer } from '../features/card-holders/list/card-holders.styles';
import useGetAllCardHolders from '../hooks/useGetAllCardHolders';
import { CardHolderType, ICardHolder } from '../interfaces/card-holder';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { FaFileCsv } from '@react-icons/all-files/fa/FaFileCsv';
import { RealmRoles } from '../constants';
import { getDateFormattedTwo } from '../utils/date';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { useLoading } from '../context/LoadingContext';


export type SearchCardHolderFilters = {
  firstName: string;
  lastName: string;
  active: boolean;
  cardHolderType: CardHolderType;
  identificationNumber?: number;
  cardholderId?: string;
};

export const CardHoldersFilters = ({ onSubmit }: { onSubmit: (data: SearchCardHolderFilters) => void }) => {
  const [form] = Form.useForm<SearchCardHolderFilters>();
  const cardHolderType = Form.useWatch('cardHolderType', form);
  const [expand, setExpand] = useState(false);

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
  };

  const onFinish = (values: SearchCardHolderFilters) => {
    onSubmit(values);
  };

  const onReset = () => {
    onSubmit({
      cardHolderType: CardHolderType.Person,
      firstName: '',
      lastName: '',
      identificationNumber: 0,
      active: true,
      cardholderId: ''
    });
  };

  return (
    <Form
      form={form}
      name="advanced_search"
      initialValues={{ cardHolderType: CardHolderType.Person, active: true }}
      style={formStyle}
      onFinish={onFinish}
      onReset={onReset}
    >
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item<SearchCardHolderFilters>
            name="firstName"
            label={cardHolderType === CardHolderType.Company ? 'Nombre Legal' : 'Nombre'}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<SearchCardHolderFilters>
            name="lastName"
            label={cardHolderType === CardHolderType.Company ? 'Nomber de Fantasia' : 'Apellido'}
          >
            <Input />
          </Form.Item>
        </Col>
        {!expand && ( <>
          <Col span={8}>
            <div style={{ textAlign: 'right' }}>
              <Space size="small">
                <Button type="primary" htmlType="submit">
                  Buscar
                </Button>
                <Button htmlType="reset">Limpiar</Button>
                <a
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setExpand(!expand);
                  }}
                >
                  <DownOutlined rotate={expand ? 180 : 0} /> Más
                </a>
              </Space>
            </div>
          </Col>
        </>
        )}
        {expand && (
          <>
            <Col span={8}>
              <Form.Item<SearchCardHolderFilters> name="identificationNumber" label={'CUIT/DNI'}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item<SearchCardHolderFilters> name="cardHolderType" label="Tipo de Titular">
                <Select
                  placeholder="Seleccionar tipo de titular"
                  options={[
                    {
                      label: 'Compañia',
                      value: CardHolderType.Company,
                    },
                    {
                      label: 'Persona',
                      value: CardHolderType.Person,
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item<SearchCardHolderFilters> name="active" label="Estado">
                <Select
                  placeholder="Seleccionar tipo de estado"
                  options={[
                    {
                      label: 'Activo',
                      value: true,
                    },
                    {
                      label: 'Desactivado',
                      value: false,
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item<SearchCardHolderFilters> name="cardholderId" label='Cardholder ID'>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <div style={{ textAlign: 'right' }}>
                <Space size="small">
                  <Button type="primary" htmlType="submit">
                    Buscar
                  </Button>
                  <Button htmlType="reset">Limpiar</Button>
                  <a
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setExpand(!expand);
                    }}
                  >
                    <DownOutlined rotate={expand ? 180 : 0} /> Menos
                  </a>
                </Space>
              </div>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

const CardHolders = () => {
  const { setLoading, setSuccess, setError } = useLoading();
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const [cardHoldersCsv, setCardHoldersCsv] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [userInfoFilter, setUserInfoFilter] = useState({
    name: '',
    lastName: '',
    identification: 0,
    active: true,
    type: CardHolderType.Person,
    cardholderId: ''
  });

  const { cardHolders, pageInfo, isLoading } = useGetAllCardHolders(
    paginationModel.page,
    paginationModel.pageSize,
    userInfoFilter
  );

  const headers = [
    { label: "realid", key: "id" },
    { label: "active", key: "active" },
    { label: "gender", key: "gender" },
    { label: "firstname", key: "first_name" },
    { label: "lastname", key: "last_name" },
    { label: "email", key: "email" },
    { label: "nationality", key: "nationality" },
    { label: "birthdate", key: "birth_date" },
    { label: "type", key: "addresses[0].type" },
    { label: "street", key: "addresses[0].street" },
    { label: "number", key: "addresses[0].number" },
    { label: "locality", key: "addresses[0].locality" },
    { label: "state", key: "addresses[0].state"},
    { label: "country", key: "addresses[0].country" },
    { label: "postal_code", key: "addresses[0].postal_code" },
    { label: "floor", key: "addresses[0].floor" },
    { label: "apartment", key: "addresses[0].apt" },
    { label: "type", key: "identifications[0].type" },
    { label: "number", key: "identifications[0].number" },
    { label: "type", key: "phones[0].type" },
    { label: "number", key: "phones[0].number" },
  ];

  useEffect(() => {
    setCardHoldersCsv(cardHolders)
  }, [cardHolders]);

  const handleMenuClick: MenuProps['onClick'] = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setSuccess({
        title: "Se ha generador el cardholder correctamente"
      })
      console.log('Action performed after 2 seconds!');
    }, 3000);

  };


  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const handleViewCardHolderDetailClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, cardHolderId: string) => {
    e.preventDefault();
    navigate(`/${issuer}/card-holders/${cardHolderId}`);
  };

  const handleSearchClick = (data: SearchCardHolderFilters) => {
    setUserInfoFilter({
      name: data.firstName,
      lastName: data.lastName,
      identification: data.identificationNumber,
      type: data.cardHolderType,
      active: data.active,
      cardholderId: data.cardholderId
    });
  };

  const NewCardHolderBtn = withAuthorization([RealmRoles.CardHolderAllWrite], () => (
      <Button type="primary" onClick={() => navigate(`/${issuer}/card-holders/new`)}>
        <Space>
          Agregar
        </Space>
      </Button>
  ));

  const columns: ColumnsType<ICardHolder> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'ID',
    },
    {
      key: 'first_name',
      dataIndex: 'first_name',
      title: 'Nombre',
      render: (_, record) => {
        return record.first_name ?? record.legal_name ?? 'No registra nombre.';
      },
    },
    {
      key: 'last_name',
      dataIndex: 'last_name',
      title: 'Apellido',
      render: (_, record) => {
        return record.last_name;
      },
    },
    {
      key: 'identifications',
      dataIndex: ['identifications'],
      title: 'Identificacion',
      render: (_, record) => {
        return (
          record.identifications?.filter(
            (identification) =>
              identification?.type === 'CUIL' || identification?.type === 'CUIT' || identification?.type === 'DNI'
          )?.[0]?.number ?? 'No registra identificacion.'
        );
      },
    },
    {
      key: 'phones',
      dataIndex: ['phones'],
      title: 'Telefono',
      render: (_, record) => {
        return record.phones && record.phones?.length > 0 && record?.phones?.[0]?.number;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => handleViewCardHolderDetailClick(e, record.id)}
            size="small"
            type="link"
            icon={<EyeOutlined />}
          >
            Detalles
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageWithFilters title="Titulares de Tarjetas">
      <PageWithFiltersContentContainer>
        <CardHoldersFilters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <CardHoldersActionsContainer>
          <CSVLink
            data={cardHoldersCsv ?? []}
            headers={headers}
            filename={`Cardholders-${getDateFormattedTwo(new Date().toDateString())}.csv`}
            target="_blank"
            title={"Descargar CSV"}
          >
            <Button type="primary">
              <Space>
                <Space style={{ paddingBottom: '5px' }}>Exportar</Space>
                <FaFileCsv style={{ fontSize: '16px', marginTop: '4px', borderRadius: '6px'}} title={"Descargar CSV"} />
              </Space>
            </Button>
          </CSVLink>
          <NewCardHolderBtn />
        </CardHoldersActionsContainer>
        <Table
          dataSource={cardHolders ?? []}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: pageInfo?.total_rows,
            showTotal: (total) => pageInfo?.total !== undefined ? `Total: ${total}` : ``,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
    </PageWithFilters>
  );
};

export default withAuthorization([RealmRoles.CardHolderAllRead, RealmRoles.CardHolderAllWrite], CardHolders);
